import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    components as Core,
    ICONS,
    MARKETPLACES_WITH_RELOGIN,
    useTranslation
} from '../../../../core';
import { css, colors, spacing, StyleSheet } from '../../../../../styles';
import * as actions from '../../../actions';
import * as selectors from '../../../selectors';
import { AdministrationActionCard } from './AdministrationActionCard';
import { ButtonWithPopover } from './ButtonWithPopover';

/**
 * Groups administrative block, sync-block, relogin, and exceeded-limit actions.
 *
 * @param {object} props
 * @param {object} props.account
 * @param {string} props.companyId
 */
export const AccountStatusButtons = ({ account, companyId }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [blockReason, setBlockReason] = useState('');
    const isBlocking = useSelector(state => selectors.isBlockingIntegration(state, account.id));
    const isUnblocking = useSelector(state => selectors.isUnblockingIntegration(state, account.id));
    const isSettingRelogin = useSelector(state => (
        selectors.isSettingReloginIntegration(state, account.id)
    ));
    const isTogglingExceededLimitBlockedIntegration = useSelector(state => (
        selectors.isTogglingExceededLimitBlockedIntegration(state, account.id)
    ));
    const supportsRelogin = MARKETPLACES_WITH_RELOGIN.includes(account.marketplace);

    const onUnblock = () => dispatch(actions.unblockIntegration(companyId, account.id));
    const onBlock = notifyAboutBlocking => {
        dispatch(actions.blockIntegration({
            companyId,
            integrationId: account.id,
            blockReason,
            notifyAboutBlocking
        }));
    };
    const onUnblockMarketSync = () => (
        dispatch(actions.unblockMarketSyncIntegration(companyId, account.id))
    );
    const onBlockMarketSync = notifyAboutBlocking => {
        dispatch(actions.blockMarketSyncIntegration({
            companyId,
            integrationId: account.id,
            blockReason,
            notifyAboutBlocking
        }));
    };
    const onToggleExceededLimitBlockedIntegration = () => (
        dispatch(actions.toggleExceededLimitBlockedIntegration(companyId, account.id))
    );
    const onReloginIntegration = () => (
        dispatch(actions.reloginIntegration(companyId, account.id))
    );
    let marketplaceSyncAction = null;

    if (account.blockedMarketSync && !account.needRelogin && !account.isDeleting) {
        marketplaceSyncAction = (
            <Core.Button
                label={t('integrations.unblockMarketSync')}
                onPress={onUnblockMarketSync}
                isLoading={isUnblocking}
            />
        );
    } else if (!account.blockedMarketSync) {
        marketplaceSyncAction = (
            <ButtonWithPopover
                label={t('integrations.blockMarketSync')}
                onPress={onBlockMarketSync}
                isLoading={isBlocking}
            />
        );
    }
    const marketplaceSyncControl = marketplaceSyncAction ? (
        <div className={css(styles.actionWithTooltip)}>
            {marketplaceSyncAction}
            <Core.HelpTooltip
                text={t('integrations.administrationDescriptions.marketplaceSync')}
            />
        </div>
    ) : null;
    const fullIntegrationDescription = t(
        'integrations.administrationDescriptions.accountAccess'
    );
    let fullIntegrationAction = null;

    if (account.isBlocked && !account.isDeleting) {
        fullIntegrationAction = (
            <Core.Button
                label={t('integrations.unblock')}
                onPress={onUnblock}
                isLoading={isUnblocking}
            />
        );
    } else if (!account.isBlocked) {
        fullIntegrationAction = (
            <ButtonWithPopover
                label={t('integrations.fullIntegrationBlock')}
                onPress={onBlock}
                isLoading={isBlocking}
            />
        );
    }
    const fullIntegrationControl = fullIntegrationAction ? (
        <div className={css(styles.actionWithTooltip)}>
            {fullIntegrationAction}
            <Core.HelpTooltip text={fullIntegrationDescription} />
        </div>
    ) : null;

    return (
        <div className={css(styles.actionRows)}>
            {!account.isExceededLimitBlocked ? (
                <AdministrationActionCard
                    title={t('integrations.administrationSections.accountAccess')}>
                    {fullIntegrationControl || marketplaceSyncControl ? (
                        <div className={css(styles.blockingControls)}>
                            {!account.isBlocked ? (
                                <Core.Input
                                    placeholder={t('integrations.blockReason')}
                                    value={blockReason}
                                    onChange={setBlockReason}
                                    className={styles.blockReasonInput}
                                    hasMargins={false}
                                />
                            ) : null}
                            <div className={css(styles.buttonsRow)}>
                                {fullIntegrationControl}
                                {marketplaceSyncControl}
                            </div>
                        </div>
                    ) : null}
                </AdministrationActionCard>
            ) : null}

            {!account.isExceededLimitBlocked && supportsRelogin ? (
                <AdministrationActionCard
                    title={t('integrations.administrationSections.authentication')}
                    description={t('integrations.administrationDescriptions.authentication')}>
                    <div className={css(styles.authenticationStatus)}>
                        <p>{t('integrations.needRelogin')}</p>
                        <Core.Tooltip
                            title={account.needRelogin
                                ? t('integrations.needReloginTooltip')
                                : t('integrations.isUnlockedTooltip')}>
                            <Core.Icon
                                type={account.needRelogin ? ICONS.lock : ICONS.unlock}
                                color={account.needRelogin ? colors.red : colors.green}
                                size={spacing.s6}
                            />
                        </Core.Tooltip>
                    </div>
                    <Core.Button
                        label={account.needRelogin
                            ? t('integrations.setRelogin')
                            : t('integrations.setNeedRelogin')}
                        onPress={onReloginIntegration}
                        isLoading={isSettingRelogin}
                    />
                </AdministrationActionCard>
            ) : null}

            <AdministrationActionCard
                title={t('integrations.administrationSections.limits')}
                description={t('integrations.administrationDescriptions.limits')}
                action={(
                    <Core.Tooltip title={t('integrations.exceededLimitBlockToolTip')}>
                        <Core.Button
                            label={account.isExceededLimitBlocked
                                ? t('integrations.exceededLimitUnblock')
                                : t('integrations.exceededLimitBlock')}
                            onPress={onToggleExceededLimitBlockedIntegration}
                            isLoading={isTogglingExceededLimitBlockedIntegration}
                        />
                    </Core.Tooltip>
                )}
            />
        </div>
    );
};

const styles = StyleSheet.create({
    actionRows: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: spacing.s1
    },
    blockingControls: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: spacing.s1,
        width: '100%',
        minWidth: 0
    },
    blockReasonInput: {
        width: '100%'
    },
    buttonsRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: spacing.s1
    },
    actionWithTooltip: {
        display: 'flex',
        alignItems: 'center',
        flex: '0 0 auto'
    },
    authenticationStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s1,
        flex: 1
    }
});
